(function () {

  /**
   * SlotMachine
   *
   * Reel layout (liquid renders 3 copies):
   *   copy 1: lines 0..(lc-1)         positions 0        .. lc-1
   *   copy 2: lines 0..(lc-1)         positions lc       .. 2lc-1
   *   copy 3: lines 0..(lc-1)         positions 2lc      .. 3lc-1
   *
   * We always run inside copy 2 (positions lc .. 2lc-1).
   * The window shows 3 lines; active is the CENTRE line (slot 1 of 3).
   * So to show line `i` of copy 2 in the centre:
   *   position = lc + i - 1   (scroll back 1 to put it in the middle slot)
   *
   * Normalise: whenever position >= 2*lc, subtract lc (jump back one copy).
   * This is invisible because copies are identical.
   *
  * Intro: spin through all lc items 5 times very fast, then decelerate.
  * Loop: one readable step every 2.4s with a smoother transition.
   */

  function SlotMachine(section) {
    this.section      = section;
    this.root         = section.querySelector('[data-slot-machine]');
    this.reel         = section.querySelector('[data-slot-reel]');
    this.lines        = section.querySelectorAll('[data-slot-line]');
    this.lineCount    = parseInt(this.root && this.root.getAttribute('data-line-count'), 10) || 0;
    this.lineHeight   = 36;
    // Start showing line 0 of copy 2 in the centre slot
    // position = lc + 0 - 1 = lc - 1
    this.position     = Math.max(0, this.lineCount - 1);
    this.activeIndex  = 0;
    this.hasStarted   = false;
    this.loopTimer    = null;
    this.pendingTimer = null;
    this.normalizeTimer = null;
    this.spinRaf      = null;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.loopIntervalMs = 2400;
    this.loopStepDurationMs = 620;
  }

  // ── Measure ──────────────────────────────────────────────────────────────

  SlotMachine.prototype.measure = function () {
    if (!this.lines.length || !this.root) return;
    var h = Math.max(24, Math.round(this.lines[0].getBoundingClientRect().height));
    if (h !== this.lineHeight) {
      this.lineHeight = h;
      this.root.style.setProperty('--slot-line-height', h + 'px');
    }
    this._applyTransform(false);
  };

  // ── Transform ────────────────────────────────────────────────────────────

  SlotMachine.prototype._applyTransform = function (animate, durationMs, easing) {
    if (!this.reel || !this.lineHeight) return;
    var offset = -this.position * this.lineHeight;
    if (animate && !this.reducedMotion) {
      this.reel.style.transition = 'transform ' + (durationMs || 550) + 'ms ' + (easing || 'cubic-bezier(0.4,0,0.2,1)');
    } else {
      this.reel.style.transition = 'none';
    }
    this.reel.style.transform = 'translate3d(0,' + offset + 'px,0)';
  };

  // ── Active highlight ──────────────────────────────────────────────────────

  SlotMachine.prototype._setActive = function (index) {
    this.activeIndex = ((index % this.lineCount) + this.lineCount) % this.lineCount;
    var active = this.activeIndex;
    this.lines.forEach(function (line) {
      line.classList.toggle('is-active', parseInt(line.getAttribute('data-line-index'), 10) === active);
    });
  };

  // ── Normalise: keep position inside copy 2 (lc-1 .. 2lc-2) ─────────────
  // If we've advanced into copy 3 territory (position >= 2*lc - 1),
  // silently jump back by exactly lc lines. Visually seamless.

  SlotMachine.prototype._normalise = function () {
    var lc = this.lineCount;
    if (lc < 1) return;
    if (this.position >= (2 * lc) - 1) {
      this.position -= lc;
    }
  };

  SlotMachine.prototype._syncActiveFromPosition = function () {
    var lc = this.lineCount;
    if (lc < 1) return;
    var inferred = Math.round(this.position) - lc + 1;
    this._setActive(inferred);
  };

  // ── Advance one line ──────────────────────────────────────────────────────

  SlotMachine.prototype._advance = function (durationMs, easing) {
    var self = this;
    var lc = this.lineCount;
    this.position += 1;
    this._setActive(this.activeIndex + 1);
    this._applyTransform(true, durationMs, easing);

    if (this.position >= (2 * lc) - 1) {
      if (this.normalizeTimer) {
        window.clearTimeout(this.normalizeTimer);
      }
      this.normalizeTimer = window.setTimeout(function () {
        self.normalizeTimer = null;
        self._normalise();
        self._applyTransform(false);
      }, (durationMs || 550) + 24);
    }
  };

  // ── Loop ─────────────────────────────────────────────────────────────────

  SlotMachine.prototype.startLoop = function () {
    if (this.loopTimer || this.spinRaf || this.reducedMotion || this.lineCount < 2) return;
    var self = this;
    this.loopTimer = window.setInterval(function () {
      self._advance(self.loopStepDurationMs, 'cubic-bezier(0.22,1,0.36,1)');
    }, this.loopIntervalMs);
  };

  SlotMachine.prototype.stopLoop = function () {
    if (this.loopTimer)    { window.clearInterval(this.loopTimer);   this.loopTimer    = null; }
    if (this.pendingTimer) { window.clearTimeout(this.pendingTimer); this.pendingTimer = null; }
    if (this.normalizeTimer) { window.clearTimeout(this.normalizeTimer); this.normalizeTimer = null; }
    if (this.spinRaf)      { window.cancelAnimationFrame(this.spinRaf); this.spinRaf = null; }
  };

  SlotMachine.prototype.restartCycle = function () {
    this.stopLoop();
    this.hasStarted = false;
    this.position = Math.max(0, this.lineCount - 1);
    this._applyTransform(false);
    this._setActive(0);
    this.startInitialAnimation();
  };

  // ── Intro: 5 fast cycles, decelerate, then slow loop ─────────────────────

  SlotMachine.prototype.startInitialAnimation = function () {
    if (this.hasStarted) { this.startLoop(); return; }
    this.hasStarted = true;

    if (this.reducedMotion || this.lineCount < 2) {
      this.startLoop();
      return;
    }

    var self = this;
    var lc = this.lineCount;
    var fastTargetLines = lc * 5;

    function runSlowdown(stepIndex) {
      var slowdownSteps = Math.max(lc + 4, 8);
      if (stepIndex >= slowdownSteps) {
        self.startLoop();
        return;
      }

      var progress = stepIndex / Math.max(1, slowdownSteps - 1);
      var eased = 1 - Math.pow(1 - progress, 3);

      // Gradually converge to the endless-loop rhythm.
      var stepDur = Math.round(90 + ((self.loopStepDurationMs - 90) * eased));
      var totalStep = Math.round(150 + ((self.loopIntervalMs - 150) * eased));
      var stepGap = Math.max(0, totalStep - stepDur);

      self._advance(stepDur, 'cubic-bezier(0.2,0.9,0.2,1)');
      self.pendingTimer = window.setTimeout(function () {
        runSlowdown(stepIndex + 1);
      }, totalStep);
    }

    function runFastSpinFrame(timestamp) {
      if (!runFastSpinFrame.lastTs) {
        runFastSpinFrame.lastTs = timestamp;
        runFastSpinFrame.linesAdvanced = 0;
        runFastSpinFrame.lastWholeLine = 0;
      }

      var dt = (timestamp - runFastSpinFrame.lastTs) / 1000;
      runFastSpinFrame.lastTs = timestamp;

      // Continuous reel speed in lines/second for seamless slot motion.
      var linesPerSecond = 24;
      var deltaLines = linesPerSecond * dt;

      runFastSpinFrame.linesAdvanced += deltaLines;
      self.position += deltaLines;
      self._normalise();
      self._applyTransform(false);

      var wholeLines = Math.floor(runFastSpinFrame.linesAdvanced);
      if (wholeLines > runFastSpinFrame.lastWholeLine) {
        self._setActive(self.activeIndex + (wholeLines - runFastSpinFrame.lastWholeLine));
        runFastSpinFrame.lastWholeLine = wholeLines;
      }

      if (runFastSpinFrame.linesAdvanced < fastTargetLines) {
        self.spinRaf = window.requestAnimationFrame(runFastSpinFrame);
        return;
      }

      // Snap to an exact line before deceleration phase.
      self.spinRaf = null;
      self.position = Math.round(self.position);
      self._normalise();
      self._applyTransform(false);
      self._syncActiveFromPosition();

      runSlowdown(0);
    }

    this.pendingTimer = window.setTimeout(function () {
      self.pendingTimer = null;
      self.spinRaf = window.requestAnimationFrame(runFastSpinFrame);
    }, 120);
  };

  // ── Init ─────────────────────────────────────────────────────────────────

  function initSection(section) {
    if (!section || section.dataset.chaosInit === 'true') return;
    if (!section.querySelector('[data-slot-machine]')) return;

    var machine = new SlotMachine(section);
    if (machine.lineCount < 1) return;
    var slotWindow = section.querySelector('[data-slot-window]');
    var observeTarget = slotWindow || section;

    machine.measure();
    machine._applyTransform(false);
    machine._setActive(0);

    var resetPill = section.querySelector('.maxcalm26-chaos-reset__reset-pill');
    if (resetPill) {
      resetPill.setAttribute('role', 'button');
      resetPill.setAttribute('tabindex', '0');

      resetPill.addEventListener('click', function () {
        machine.restartCycle();
      });

      resetPill.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          machine.restartCycle();
        }
      });
    }

    var resizeRaf = null;
    window.addEventListener('resize', function () {
      if (resizeRaf) window.cancelAnimationFrame(resizeRaf);
      resizeRaf = window.requestAnimationFrame(function () { machine.measure(); });
    }, { passive: true });

    var observer = new IntersectionObserver(function (entries) {
      var entry = entries[0];
      if (!entry || entry.target !== observeTarget) return;

      var inView = entry.isIntersecting && entry.intersectionRatio >= 0.3;

      if (inView) {
        if (!machine.hasStarted) {
          machine.startInitialAnimation();
        } else {
          machine.startLoop();
        }
      } else {
        machine.stopLoop();
      }
    }, {
      threshold: [0, 0.3],
      root: null,
      rootMargin: '0px'
    });

    observer.observe(observeTarget);
    section.dataset.chaosInit = 'true';
  }

  function initAll() {
    document.querySelectorAll('[data-maxcalm26-chaos-reset]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) { initSection(e.target); });

})();
